// The "Data Error" tag is set by the indexer when bogg's sanity checks on a
// trader's statistics fail. The indexer sends the reasons as `data_issues`:
// bogg's own violation codes, e.g. "neg:portfolio_value" or "range:win_rate".
// Admins see the reasons in the tag's tooltip; everyone else sees the plain
// description.

export const DATA_ERROR_TAG = 'Data Error';

const REL_TEXT: Record<string, string> = {
    fills_vs_total_trades: 'maker and taker fills do not add up to the trades',
    win_gt_closed: 'more wins than closed markets',
    closed_gt_unique_markets: 'more closed markets than markets traded',
    first_seen_after_last_seen: 'first trade after the last trade',
};

function fieldName(field: string): string {
    return field.replace(/_/g, ' ');
}

/** One reason code as short readable text; an unknown code is returned as is. */
export function describeDataIssue(code: string): string {
    const sep = code.indexOf(':');
    if (sep <= 0) return code;
    const kind = code.slice(0, sep);
    const rest = code.slice(sep + 1);
    if (!rest) return code;
    switch (kind) {
        case 'neg':
            return `negative ${fieldName(rest)}`;
        case 'nonfinite':
            return `${fieldName(rest)} is not a number`;
        case 'range':
            return `${fieldName(rest)} out of range`;
        case 'rel':
            return REL_TEXT[rest] ?? code;
        case 'sanity':
            // The indexer's placeholder for a failed check with no details.
            return rest === 'failed' ? 'sanity checks failed' : code;
        default:
            return code;
    }
}

/**
 * The tooltip text for a tag. Only the Data Error tag of an admin, with at
 * least one reason, gets the reasons added; every other case returns the
 * description unchanged.
 */
export function tagTooltipText(
    tag: string,
    description: string | undefined,
    dataIssues: readonly string[] | null | undefined,
    isAdmin: boolean
): string | undefined {
    if (!isAdmin || tag !== DATA_ERROR_TAG || !dataIssues?.length) {
        return description;
    }
    const reasons = `Reasons: ${dataIssues.map(describeDataIssue).join('; ')}.`;
    return description ? `${description} ${reasons}` : reasons;
}
